import image3dPreviewImg from 'assets/images/aistudio/image3d_preview.png';
import multiview3dPreviewImg from 'assets/images/aistudio/multiview3d_preview.png';
import text3dPreviewImg from 'assets/images/aistudio/text3d_preview.png';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import DiceIcon from 'src/shared/material_icons/places/casino.svg';
import PaperPlaneIcon from 'src/shared/material_icons/content/send.svg';
import TimesIcon from 'src/shared/material_icons/navigation/close.svg';
import styled, { css, keyframes } from 'styled-components';
import { Icon } from '../../../../widgets/icon';
import { urlToFile } from 'src/shared/helpers';
import { MAX_3D_IMAGE_DIMENSION, MAX_3D_UPLOAD_SIZE_MB } from '../../constants';
import { Model3DParameterValues } from '../../modelGeneration3DParameters';
import { Model3DModelSelections, ModelGeneration3DModel, ToolType } from '../../types';
import { PromptInput } from '../inputs/promptInput';
import { AiStudioHelpLink, AiStudioHelpRow } from '../shared/aiStudioHelpLink';
import { ImageUploadZones } from '../shared/imageUploadZones';
import { ToolEmptyStateImage } from '../shared/ToolEmptyStateImage';
import { getDefaultModelGeneration3DModel, isModelGeneration3DModelSupported, Model3DSettingsSidebar } from './model3DSettingsSidebar';
import { getSupportedMultiViewSlotIds, MultiViewDropZone, VIEW_SLOTS, type ViewSlotId } from './text3DWorkspaceView';

const CARD_PREVIEW_IMAGES: Record<number, string> = {
    0: text3dPreviewImg,
    1: image3dPreviewImg,
    2: multiview3dPreviewImg
};

/* ─── Card data ──────────────────────────────────────────────────────────── */

interface CardDef {
    title: string;
    description: string;
    expandedSubtitle: string;
    agentId: string;
    promptPlaceholder: string;
}

const CARDS: CardDef[] = [
    {
        title: 'Text to 3D',
        description: 'Generate high-fidelity 3D models and assets directly from simple text prompts.',
        expandedSubtitle: 'Type a description — our AI interprets every word and sculpts a high-fidelity 3D asset in moments. No modeling skills needed.',
        agentId: 'text-to-3d',
        promptPlaceholder: 'Describe your 3D model, see it come to life...'
    },
    {
        title: 'Image to 3D',
        description: 'Transform 2D images and sketches into detailed 3D geometry instantly.',
        expandedSubtitle: 'Drop in any photo, sketch, or reference image and watch the AI reconstruct it as an accurate, textured, high-fidelity 3D model.',
        agentId: 'image-to-3d',
        promptPlaceholder: 'Upload or describe your image, see it in 3D...'
    },
    {
        title: 'Multi-View to 3D',
        description: 'Create accurate 3D models using 1-8 standard multi-view image references.',
        expandedSubtitle: 'Build accurate 3D models from 1–8 angles with AI-powered geometry and depth capture.',
        agentId: 'multi-views-to-3d',
        promptPlaceholder: 'Describe the object from multiple views, see it in 3D...'
    }
];

/* ─── Keyframes ──────────────────────────────────────────────────────────── */

const gradientShift = keyframes`
	0%   { background-position: 0% center; }
	50%  { background-position: 100% center; }
	100% { background-position: 0% center; }
`;

const fadeIn = keyframes`
	from { opacity: 0; }
	to   { opacity: 1; }
`;

/* Animated orb drifting paths */
const orb1Drift = keyframes`
	0%   { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
	25%  { transform: translate(-50%, -50%) translate(40px, -30px) scale(1.1); }
	50%  { transform: translate(-50%, -50%) translate(-20px, 50px) scale(0.95); }
	75%  { transform: translate(-50%, -50%) translate(60px, 20px) scale(1.05); }
	100% { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
`;

const orb2Drift = keyframes`
	0%   { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
	33%  { transform: translate(-50%, -50%) translate(-50px, 40px) scale(1.08); }
	66%  { transform: translate(-50%, -50%) translate(30px, -60px) scale(0.92); }
	100% { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
`;

const orb3Drift = keyframes`
	0%   { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
	20%  { transform: translate(-50%, -50%) translate(50px, 30px) scale(0.9); }
	50%  { transform: translate(-50%, -50%) translate(-40px, -20px) scale(1.12); }
	80%  { transform: translate(-50%, -50%) translate(20px, 60px) scale(1.0); }
	100% { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
`;

const orb4Drift = keyframes`
	0%   { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
	40%  { transform: translate(-50%, -50%) translate(-30px, -50px) scale(1.06); }
	70%  { transform: translate(-50%, -50%) translate(45px, 25px) scale(0.94); }
	100% { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
`;

/* ─── Shared gradient text (AI Studio purple/pink palette) ───────────────── */

const gradientText = css`
    background: linear-gradient(90deg, #7c3aed, #a855f7, #ec4899, #a855f7, #7c3aed);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 8s ease infinite;
`;

/* ─── Layout ─────────────────────────────────────────────────────────────── */

const Wrapper = styled.div<{ $expanded: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: ${(p) => (p.$expanded ? 'stretch' : 'center')};
    justify-content: ${(p) => (p.$expanded ? 'flex-start' : 'center')};
    padding: ${(p) => (p.$expanded ? '0' : '32px 48px')};
    background: ${(props) => props.theme.app.backgroundColor};
    overflow-y: ${(p) => (p.$expanded ? 'hidden' : 'auto')};
    overflow-x: hidden;
    box-sizing: border-box;
`;

/* 3 cards per row × 320px + 2 gaps × 16px */
const CARDS_TOTAL_WIDTH = 3 * 320 + 2 * 16;

const DescriptionLine1 = styled.p`
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 6px 0;
    padding-top: 16px;
    line-height: 1.3;
    text-align: center;
    width: ${CARDS_TOTAL_WIDTH}px;
    ${gradientText}
`;

const DescriptionLine2 = styled.p`
    font-size: 16px;
    font-weight: 400;
    margin: 0 0 64px 0;
    line-height: 1.5;
    text-align: center;
    width: ${CARDS_TOTAL_WIDTH}px;
    ${gradientText}
`;

/* ─── Cards grid ─────────────────────────────────────────────────────────── */

/* Defined before Card so it can be referenced in the Card hover selector */
const StartCreatingBtn = styled.button`
    width: 100%;
    padding: 8px 0;
    border: none;
    border-radius: 8px;
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    color: #ffffff;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    opacity: 0;
    pointer-events: none;
    transition:
        opacity 0.2s ease,
        filter 0.18s ease;

    &:hover {
        filter: brightness(1.08);
    }
`;

const CardsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 320px);
    gap: 16px;
    justify-content: center;
    animation: ${fadeIn} 0.25s ease;
`;

const Card = styled.div`
    width: 320px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1.5px solid #ede9fe;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(124, 58, 237, 0.06);
    transition:
        transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
        border-color 0.22s ease,
        box-shadow 0.22s ease;
    cursor: pointer;

    &:hover {
        transform: scale(1.06) translateY(-2px);
        border-color: #a855f7;
        box-shadow: 0 10px 28px rgba(124, 58, 237, 0.18);
        z-index: 1;
    }

    &:hover ${StartCreatingBtn} {
        opacity: 1;
        pointer-events: auto;
    }
`;

const CardPreviewImage = styled.img`
    width: 100%;
    height: 130px;
    object-fit: contain;
    background: #07111d;
    flex-shrink: 0;
    display: block;
`;

const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 14px;
    gap: 8px;
`;

const CardTitle = styled.h3`
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    color: ${(props) => props.theme.app.textColor};
    line-height: 1.3;
`;

const CardDesc = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
    flex: 1;
`;

/* ─── Expanded state — AI Studio purple / fuchsia palette ────────────────── */

const ExpandedContainer = styled.div`
    position: relative;
    width: 100%;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(160deg, #fdfcff 0%, #faf8ff 50%, #fffafc 100%);
    animation: ${fadeIn} 0.3s ease forwards;
`;

const ExpandedShell = styled.div`
    flex: 1;
    width: 100%;
    min-height: 0;
    display: flex;
`;

const BlurOrb1 = styled.div<{ $mx: number; $my: number }>`
    position: absolute;
    top: 0%;
    left: 22%;
    width: 540px;
    height: 540px;
    border-radius: 50%;
    background: rgba(124, 58, 237, 0.07);
    filter: blur(100px);
    pointer-events: none;
    transform: translate(-50%, -50%) translate(${(p) => p.$mx * 22}px, ${(p) => p.$my * 18}px);
    animation: ${orb1Drift} 12s ease-in-out infinite;
    transition: transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const BlurOrb2 = styled.div<{ $mx: number; $my: number }>`
    position: absolute;
    top: 100%;
    left: 12%;
    width: 440px;
    height: 440px;
    border-radius: 50%;
    background: rgba(168, 85, 247, 0.06);
    filter: blur(95px);
    pointer-events: none;
    transform: translate(-50%, -50%) translate(${(p) => p.$mx * -18}px, ${(p) => p.$my * 14}px);
    animation: ${orb2Drift} 16s ease-in-out infinite;
    transition: transform 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const BlurOrb3 = styled.div<{ $mx: number; $my: number }>`
    position: absolute;
    top: 25%;
    left: 88%;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: rgba(236, 72, 153, 0.06);
    filter: blur(85px);
    pointer-events: none;
    transform: translate(-50%, -50%) translate(${(p) => p.$mx * 14}px, ${(p) => p.$my * -20}px);
    animation: ${orb3Drift} 14s ease-in-out infinite;
    transition: transform 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const BlurOrb4 = styled.div<{ $mx: number; $my: number }>`
    position: absolute;
    top: 85%;
    left: 68%;
    width: 320px;
    height: 320px;
    border-radius: 50%;
    background: rgba(139, 92, 246, 0.12);
    filter: blur(80px);
    pointer-events: none;
    transform: translate(-50%, -50%) translate(${(p) => p.$mx * -10}px, ${(p) => p.$my * -12}px);
    animation: ${orb4Drift} 18s ease-in-out infinite;
    transition: transform 1.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const Content = styled.div`
    position: relative;
    z-index: 5;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 80px 40px 40px;
    width: 100%;
    box-sizing: border-box;

    @media (max-height: 720px) {
        padding: 48px 40px 28px;
        gap: 8px;
    }

    @media (max-height: 660px) {
        padding: 32px 40px 20px;
        gap: 6px;
    }
`;

const ControlsCard = styled.div`
    width: 100%;
    max-width: 860px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid rgb(255, 255, 255);
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.45);
    box-sizing: border-box;
`;

const PlaceholderWrapper = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const RoundGenerateButton = styled.button`
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        transform 0.2s ease,
        opacity 0.2s ease;

    &:hover:not(:disabled) {
        transform: scale(1.05);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    ${Icon} {
        width: 24px;
        height: 24px;
        fill: currentColor;
    }
`;

const InputPreviews = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    align-items: center;
`;

const InputPreviewItem = styled.div`
    position: relative;
    height: 58px;
    min-width: 58px;
    max-width: 150px;
    flex-shrink: 0;
`;

const InputPreviewImage = styled.div`
    height: 100%;
    width: auto;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.backgroundColor};
    display: flex;
    align-items: center;
    justify-content: center;

    img {
        height: 100%;
        width: auto;
        max-width: 150px;
        object-fit: contain;
        display: block;
    }
`;

const InputPreviewRemove = styled.button`
    position: absolute;
    top: 0px;
    right: 0px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
        background: #dc2626;
    }

    ${Icon} {
        width: 8px;
        height: 8px;
        fill: currentColor;
    }
`;

const ExpandedTitle = styled.h2`
    font-size: 38px;
    font-weight: 700;
    color: #1e0a3c;
    margin: 0;
    line-height: 1.2;
    text-align: center;
    white-space: nowrap;

    @media (max-height: 720px) {
        font-size: 30px;
    }

    @media (max-height: 660px) {
        font-size: 24px;
    }
`;

/* AI Studio fuchsia/violet accent */
const ExpandedTitleAccent = styled.span`
    background: linear-gradient(90deg, #7c3aed, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const ExpandedSubtitle = styled.p`
    font-size: 13px;
    font-weight: 400;
    color: rgba(30, 10, 60, 0.58);
    margin: 0;
    line-height: 1.6;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;

const PromptRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
`;

/* ─── Random prompts for Text to 3D ─────────────────────────────────────── */

const TEXT_TO_3D_RANDOM_PROMPTS = [
    'A white ceramic coffee mug with a simple handle, product shot ready',
    'A leather wallet with stitched edges and card slots, ecommerce style',
    'A glass perfume bottle with a gold cap and minimalist label',
    'A pair of white running sneakers with a rubber sole, front view',
    'A stainless steel water bottle with a screw-top lid, matte finish',
    'A wooden cutting board with carved handles, kitchen product',
    'A round scented candle in a glass jar with a cotton wick',
    'A sports watch with a silicone strap and digital display',
    'A cotton tote bag with short handles and a front pocket',
    'A hardcover notebook with elastic closure and ribbon bookmark',
    'A set of wireless earbuds in a compact charging case',
    'A olive oil bottle with a cork stopper and printed label'
];

const InputWithDiceWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const DiceBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 0;
    padding: 4px;
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 6px;
    overflow: hidden;
    transition:
        color 0.15s ease,
        background 0.15s ease,
        gap 0.15s ease,
        padding 0.15s ease;

    ${Icon} {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        fill: currentColor;
    }

    &:hover {
        color: #7c3aed;
        background: rgba(124, 58, 237, 0.07);
        gap: 5px;
        padding: 4px 8px 4px 6px;
    }
`;

const DiceBtnLabel = styled.span`
    font-size: 11px;
    max-width: 0;
    overflow: hidden;
    opacity: 0;
    white-space: nowrap;
    transition:
        max-width 0.2s ease,
        opacity 0.15s ease;

    ${DiceBtn}:hover & {
        max-width: 140px;
        opacity: 1;
    }
`;

const DiceRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

/* ─── Multi-view slot definitions ────────────────────────────────────────── */

type MvSlotValue = { file: File; previewUrl: string; name: string } | null;
type MvSlots = Record<ViewSlotId, MvSlotValue>;

const createEmptyMvSlots = (): MvSlots =>
    VIEW_SLOTS.reduce((acc, slot) => {
        acc[slot.id] = null;
        return acc;
    }, {} as MvSlots);

/* ─── Component ──────────────────────────────────────────────────────────── */

interface Model3DLandingViewProps {
    onStartCreating?: (
        agentId: string,
        prompt: string,
        imageFile?: File,
        imageUrl?: string,
        multiViewReady?: boolean,
        mvFiles?: Array<{ file: File; viewType: string }>
    ) => void;
    onOpenCatalog?: () => void;
    onSelectFromLibrary?: (onSelect: (imageUrl: string) => void) => void;
    modelGeneration3DModels?: readonly ModelGeneration3DModel[];
    model3DModelSelections?: Model3DModelSelections;
    onModel3DModelChange?: (agentId: string, model: string) => void;
    getModel3DParameterValues?: (agentId: string | null | undefined) => Model3DParameterValues | undefined;
    onModel3DParameterValuesChange?: (agentId: string, model: string, values: Model3DParameterValues) => void;
    /** Pre-select a card by agentId and skip the card-grid step */
    initialAgentId?: string;
    /** Product image URL from the catalog/iframe flow. When set, it pre-fills the multi-views-to-3d
     *  "front" slot so the user can extend with additional angles instead of starting from scratch. */
    productImageUrl?: string;
}

export const Model3DLandingView: FC<Model3DLandingViewProps> = ({
    onStartCreating,
    onOpenCatalog,
    onSelectFromLibrary,
    modelGeneration3DModels = [],
    model3DModelSelections,
    onModel3DModelChange,
    getModel3DParameterValues,
    onModel3DParameterValuesChange,
    initialAgentId,
    productImageUrl
}) => {
    const initialIdx = initialAgentId ? CARDS.findIndex((c) => c.agentId === initialAgentId) : -1;
    const [selectedCardIdx, setSelectedCardIdx] = useState<number | null>(initialIdx >= 0 ? initialIdx : null);
    const [promptText, setPromptText] = useState('');
    const [multiViewHasImage, setMultiViewHasImage] = useState(false);
    const [multiViewLoading, setMultiViewLoading] = useState(false);
    const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isFileObjectUrl, setIsFileObjectUrl] = useState(false);
    const [mvSlots, setMvSlots] = useState<MvSlots>(() => createEmptyMvSlots());
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [isTransitioning, setIsTransitioning] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const selectedAgentId = selectedCardIdx !== null ? CARDS[selectedCardIdx].agentId : null;
    const selectedGenerationModel = useMemo(() => {
        const selectedModel = selectedAgentId ? model3DModelSelections?.[selectedAgentId as ToolType] : undefined;
        if (isModelGeneration3DModelSupported(modelGeneration3DModels, selectedAgentId, selectedModel)) {
            return selectedModel;
        }
        return getDefaultModelGeneration3DModel(modelGeneration3DModels, selectedAgentId);
    }, [modelGeneration3DModels, model3DModelSelections, selectedAgentId]);
    const selectedGenerationModelInfo = useMemo(
        () => modelGeneration3DModels.find((model) => model.model === selectedGenerationModel && model.supportedModes.includes(selectedAgentId ?? '')),
        [modelGeneration3DModels, selectedAgentId, selectedGenerationModel]
    );
    const supportedMultiViewSlotIds = useMemo(() => getSupportedMultiViewSlotIds(selectedGenerationModelInfo), [selectedGenerationModelInfo]);

    // Revoke object URL only when it's a local blob URL (not a library URL)
    useEffect(() => {
        return () => {
            if (previewUrl && isFileObjectUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl, isFileObjectUrl]);

    const handleImageUpload = useCallback((file: File) => {
        setUploadedImageFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setIsFileObjectUrl(true);
    }, []);

    const handleLibrarySelect = useCallback(() => {
        onSelectFromLibrary?.((url) => {
            setUploadedImageFile(null);
            setPreviewUrl(url);
            setIsFileObjectUrl(false);
        });
    }, [onSelectFromLibrary]);

    const handleRemoveImage = useCallback(() => {
        setUploadedImageFile(null);
        setPreviewUrl(null);
        setIsFileObjectUrl(false);
    }, []);

    useEffect(() => {
        setMultiViewHasImage(supportedMultiViewSlotIds.some((slotId) => mvSlots[slotId] !== null));
    }, [mvSlots, supportedMultiViewSlotIds]);

    useEffect(() => {
        // Cleanup all mv slot blob URLs on unmount
        return () => {
            Object.values(mvSlots).forEach((s) => s && URL.revokeObjectURL(s.previewUrl));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pre-fetch the product image as a File so MultiViewDropZone can mount with the "front"
    // slot already filled when the user selects the multi-views-to-3d card. MultiViewDropZone
    // creates its own preview URLs from seeded files, so the URL we generate here is only used
    // as the cache-key for the dropzone's remount.
    const [productFrontFile, setProductFrontFile] = useState<{ file: File; previewUrl: string; name: string } | null>(null);
    const productFrontFileRef = useRef<{ file: File; previewUrl: string; name: string } | null>(null);
    productFrontFileRef.current = productFrontFile;
    useEffect(() => {
        if (!productImageUrl) {
            setProductFrontFile(null);
            return;
        }
        let cancelled = false;
        urlToFile(productImageUrl, 'product-image.png')
            .then((file) => {
                if (cancelled) return;
                setProductFrontFile({ file, previewUrl: URL.createObjectURL(file), name: file.name });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [productImageUrl]);

    useEffect(() => {
        return () => {
            if (productFrontFileRef.current) URL.revokeObjectURL(productFrontFileRef.current.previewUrl);
        };
    }, []);

    // Seed the image-to-3d preview with the product image when the user enters that card.
    // Tracked via a ref so that, once the user uploads/removes/picks a different image, we
    // don't keep re-overriding their choice. The flag is reset in handleCardClick so the
    // seed can apply again the next time the card is selected.
    const seededImageToRef = useRef(false);
    useEffect(() => {
        if (!productImageUrl) return;
        if (selectedAgentId !== 'image-to-3d') return;
        if (seededImageToRef.current) return;
        if (previewUrl) return;
        seededImageToRef.current = true;
        setPreviewUrl(productImageUrl);
        setIsFileObjectUrl(false);
    }, [productImageUrl, selectedAgentId, previewUrl]);

    const handleCardClick = (idx: number) => {
        setSelectedCardIdx(idx);
        setPromptText('');
        setMultiViewHasImage(false);
        setMultiViewLoading(false);
        setUploadedImageFile(null);
        setPreviewUrl(null);
        setIsFileObjectUrl(false);
        // Re-arm the image-to-3d product-image seed for the next time the card is entered.
        seededImageToRef.current = false;
        setMvSlots((prev) => {
            Object.values(prev).forEach((s) => s && URL.revokeObjectURL(s.previewUrl));
            return createEmptyMvSlots();
        });
    };

    const handleSubmit = () => {
        if (selectedCardIdx === null || isTransitioning) return;
        if (!selectedGenerationModel) return;
        const card = CARDS[selectedCardIdx];
        setIsTransitioning(true);

        // Collect multi-view files in slot order (front first, required) so the workspace can
        // send the front view as imageUrl and the rest as multiViewImages.
        const mvFiles: Array<{ file: File; viewType: string }> | undefined =
            card.agentId === 'multi-views-to-3d'
                ? supportedMultiViewSlotIds.reduce<Array<{ file: File; viewType: string }>>((acc, slotId) => {
                      const entry = mvSlots[slotId];
                      if (entry) acc.push({ file: entry.file, viewType: slotId });
                      return acc;
                  }, [])
                : undefined;

        setTimeout(() => {
            onStartCreating?.(
                card.agentId,
                promptText,
                card.agentId === 'image-to-3d' ? (uploadedImageFile ?? undefined) : undefined,
                card.agentId === 'image-to-3d' && !uploadedImageFile ? (previewUrl ?? undefined) : undefined,
                card.agentId === 'multi-views-to-3d' ? multiViewHasImage : undefined,
                mvFiles
            );
        }, 350);
    };

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        // Normalize to [-1, 1]
        const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
        const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
        setMousePos({ x, y });
    }, []);

    const selectedCard = selectedCardIdx !== null ? CARDS[selectedCardIdx] : null;

    return (
        <Wrapper $expanded={selectedCard !== null}>
            {selectedCard === null && (
                <>
                    <DescriptionLine1>{T._('A team of AI agents working for you', 'Admin')}</DescriptionLine1>
                    <DescriptionLine2>
                        {T._('Bring products to life with AI-powered tools to create 3D model, and many other features around', 'Admin')}
                    </DescriptionLine2>
                </>
            )}

            {selectedCard === null ? (
                <CardsRow>
                    {CARDS.map((card, idx) => (
                        <Card key={card.title} onClick={() => handleCardClick(idx)}>
                            {CARD_PREVIEW_IMAGES[idx] && <CardPreviewImage src={CARD_PREVIEW_IMAGES[idx]} alt={card.title} />}
                            <CardContent>
                                <CardTitle>{T._(card.title, 'Admin')}</CardTitle>
                                <CardDesc>{T._(card.description, 'Admin')}</CardDesc>
                                <StartCreatingBtn>{T._('Start Creating', 'Admin')}</StartCreatingBtn>
                            </CardContent>
                        </Card>
                    ))}
                </CardsRow>
            ) : (
                <ExpandedShell>
                    <ExpandedContainer ref={containerRef} onMouseMove={handleMouseMove} style={{ overflow: 'hidden' }}>
                        {/* Animated blur orbs — respond to mouse movement */}
                        <BlurOrb1 $mx={mousePos.x} $my={mousePos.y} />
                        <BlurOrb2 $mx={mousePos.x} $my={mousePos.y} />
                        <BlurOrb3 $mx={mousePos.x} $my={mousePos.y} />
                        <BlurOrb4 $mx={mousePos.x} $my={mousePos.y} />

                        <Content>
                            <ExpandedTitle>
                                {T._('Start creating with', 'Admin')} <ExpandedTitleAccent>{T._(selectedCard.title, 'Admin')}</ExpandedTitleAccent>
                            </ExpandedTitle>

                            <ExpandedSubtitle>{T._(selectedCard.expandedSubtitle, 'Admin')}</ExpandedSubtitle>

                            <PlaceholderWrapper>
                                <ToolEmptyStateImage toolId={selectedCard.agentId as ToolType} />
                            </PlaceholderWrapper>

                            {selectedCard.agentId === 'image-to-3d' ? (
                                previewUrl ? (
                                    <ControlsCard>
                                        <InputPreviews>
                                            <InputPreviewItem>
                                                <InputPreviewImage>
                                                    <img src={previewUrl} alt={uploadedImageFile?.name ?? 'Library image'} />
                                                </InputPreviewImage>
                                                <InputPreviewRemove onClick={handleRemoveImage} title={T._('Remove', 'Admin')}>
                                                    <Icon>
                                                        <TimesIcon />
                                                    </Icon>
                                                </InputPreviewRemove>
                                            </InputPreviewItem>
                                        </InputPreviews>
                                        <PromptRow>
                                            <PromptInput
                                                value=''
                                                onChange={() => {}}
                                                placeholder={T._('Prompt not supported for this tool', 'Admin')}
                                                disabled
                                            />
                                            <RoundGenerateButton
                                                onClick={handleSubmit}
                                                disabled={!selectedGenerationModel}
                                                title={T._('Start Creating', 'Admin')}
                                            >
                                                <Icon>
                                                    <PaperPlaneIcon />
                                                </Icon>
                                            </RoundGenerateButton>
                                        </PromptRow>
                                        <AiStudioHelpRow>
                                            <AiStudioHelpLink agentId={selectedCard.agentId} />
                                        </AiStudioHelpRow>
                                    </ControlsCard>
                                ) : (
                                    <ControlsCard>
                                        <ImageUploadZones
                                            onImageUpload={handleImageUpload}
                                            onOpenCatalog={onOpenCatalog}
                                            onOpenLibrary={onSelectFromLibrary ? handleLibrarySelect : undefined}
                                            maxSizeMB={MAX_3D_UPLOAD_SIZE_MB}
                                            maxDimension={MAX_3D_IMAGE_DIMENSION}
                                        />
                                        <AiStudioHelpRow>
                                            <AiStudioHelpLink agentId={selectedCard.agentId} />
                                        </AiStudioHelpRow>
                                    </ControlsCard>
                                )
                            ) : selectedCard.agentId === 'multi-views-to-3d' ? (
                                <ControlsCard>
                                    <MultiViewDropZone
                                        key={productFrontFile?.previewUrl ?? 'no-seed'}
                                        initialSlots={productFrontFile && supportedMultiViewSlotIds.includes('front') ? { front: productFrontFile } : undefined}
                                        slotIds={supportedMultiViewSlotIds}
                                        onAnyFilled={setMultiViewHasImage}
                                        onAnyLoading={setMultiViewLoading}
                                        onSlotsChange={(slots) => {
                                            setMvSlots((prev) => {
                                                const next = { ...prev };
                                                VIEW_SLOTS.forEach((slot) => {
                                                    next[slot.id] = slots[slot.id] ?? null;
                                                });
                                                return next;
                                            });
                                        }}
                                    />
                                    {multiViewHasImage && (
                                        <PromptRow>
                                            <PromptInput
                                                value=''
                                                onChange={() => {}}
                                                placeholder={T._('Prompt not supported for this tool', 'Admin')}
                                                disabled
                                            />
                                            <RoundGenerateButton
                                                onClick={handleSubmit}
                                                disabled={!mvSlots.front || multiViewLoading || !selectedGenerationModel}
                                                title={
                                                    multiViewLoading
                                                        ? T._('Waiting for image upload to finish…', 'Admin')
                                                        : mvSlots.front
                                                          ? T._('Start Creating', 'Admin')
                                                          : T._('Upload the Front view first', 'Admin')
                                                }
                                            >
                                                <Icon>
                                                    <PaperPlaneIcon />
                                                </Icon>
                                            </RoundGenerateButton>
                                        </PromptRow>
                                    )}
                                    <AiStudioHelpRow>
                                        <AiStudioHelpLink agentId={selectedCard.agentId} />
                                    </AiStudioHelpRow>
                                </ControlsCard>
                            ) : (
                                <ControlsCard>
                                    <PromptRow>
                                        <InputWithDiceWrapper>
                                            <PromptInput
                                                value={promptText}
                                                onChange={setPromptText}
                                                placeholder={T._(selectedCard.promptPlaceholder, 'Admin')}
                                            />
                                        </InputWithDiceWrapper>
                                        <RoundGenerateButton
                                            onClick={handleSubmit}
                                            disabled={!promptText.trim() || !selectedGenerationModel}
                                            title={T._('Start Creating', 'Admin')}
                                        >
                                            <Icon>
                                                <PaperPlaneIcon />
                                            </Icon>
                                        </RoundGenerateButton>
                                    </PromptRow>
                                    <DiceRow>
                                        <DiceBtn
                                            type='button'
                                            onClick={() =>
                                                setPromptText(TEXT_TO_3D_RANDOM_PROMPTS[Math.floor(Math.random() * TEXT_TO_3D_RANDOM_PROMPTS.length)])
                                            }
                                        >
                                            <Icon>
                                                <DiceIcon />
                                            </Icon>
                                            <DiceBtnLabel>{T._('Try random prompt', 'Admin')}</DiceBtnLabel>
                                        </DiceBtn>
                                        <AiStudioHelpLink agentId={selectedCard.agentId} />
                                    </DiceRow>
                                </ControlsCard>
                            )}
                        </Content>
                    </ExpandedContainer>
                    <Model3DSettingsSidebar
                        agentId={selectedCard.agentId}
                        models={modelGeneration3DModels}
                        selectedModel={selectedGenerationModel}
                        onModelChange={onModel3DModelChange}
                        parameterValues={getModel3DParameterValues?.(selectedCard.agentId)}
                        onParameterValuesChange={onModel3DParameterValuesChange}
                    />
                </ExpandedShell>
            )}
        </Wrapper>
    );
};

export default Model3DLandingView;
